/**
 * MediaCompareSlot — Original ⇄ Revised in-body compare for a scheduled post&apos;s
 * media edit (spec §5.3). The AI-temporary surface contract (DESIGN-PATTERNS
 * §2.6): a dashed-skill provisional frame, SparkleIcon provenance, skeleton
 * intentionally square (aspect-square) while the Keep round-trip runs (§2.8) —
 * in-inspector compare previews are always square regardless of the post&apos;s
 * aspect ratio. Keep = bg-skill/10 text-skill (the AI-accept action); Revert =
 * monochrome (neutral exit, never purple — §2.6).
 *
 * layout: &apos;side-by-side&apos; on a desktop full slot; &apos;stacked&apos; for mobile and the
 * narrow carousel slot toggle.
 */
import { ArrowCounterClockwiseIcon, CheckIcon, SparkleIcon } from '@phosphor-icons/react';

import type { PendingCompare } from './usePostMediaEdit';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface MediaCompareSlotProps {
  compare: PendingCompare;
  busy: boolean;
  layout: 'side-by-side' | 'stacked';
  onKeep(): void;
  onRevert(): void;
}

export function MediaCompareSlot({
  compare,
  busy,
  layout,
  onKeep,
  onRevert,
}: MediaCompareSlotProps) {
  const stacked = layout === 'stacked';
  return (
    <div className="space-y-2 rounded-xl border border-dashed border-skill/30 bg-skill/[0.04] p-2.5">
      <div className="flex items-center gap-1.5">
        <SparkleIcon className="size-3 text-skill/70" weight="duotone" aria-hidden />
        <span className="text-detail font-medium text-skill/80">Original vs revised</span>
      </div>
      <div className={cn('grid gap-2', stacked ? 'grid-cols-1' : 'grid-cols-2')}>
        <figure className="space-y-1">
          <img
            src={compare.originalUrl}
            alt="Original"
            loading="lazy"
            className="aspect-square w-full rounded-lg border border-border/40 object-cover"
          />
          <figcaption className="text-micro text-muted-foreground/70">Original</figcaption>
        </figure>
        <figure className="space-y-1" data-testid="compare-revised" aria-busy={busy}>
          {busy ? (
            // Skeleton shaped to the media aspect (§2.8), skill-tinted, reduced-motion safe.
            <div
              className="aspect-square w-full animate-pulse rounded-lg bg-skill/10 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <img
              src={compare.revisedUrl}
              alt="Revised"
              loading="lazy"
              className="aspect-square w-full rounded-lg border border-dashed border-skill/40 object-cover"
            />
          )}
          <figcaption className="text-micro text-skill/80">Revised</figcaption>
        </figure>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="flat"
          className="flex-1 bg-skill/10 text-skill hover:bg-skill/15"
          disabled={busy}
          onClick={onKeep}
        >
          <CheckIcon className="size-3.5" weight="bold" />
          Keep
        </Button>
        <Button
          size="sm"
          variant="flat"
          className="flex-1 hover:text-foreground"
          disabled={busy}
          onClick={onRevert}
        >
          <ArrowCounterClockwiseIcon className="size-3.5" weight="regular" />
          Revert
        </Button>
      </div>
    </div>
  );
}
